import classNames from 'classnames';
import React, { ChangeEventHandler, ReactNode, Ref } from 'react';

import { useUniqueId } from 'utils/hooks';

import styles from './Input.module.scss';

type Status = 'neutral' | 'success' | 'error';
type Presentation = 'default' | 'contrast' | 'light-border';

type Props = {
  inputProps: {
    id?: string;
    ref?: Ref<HTMLInputElement>;
    placeholder?: string;
    value: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: () => void;
    onFocus?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    'data-qa-id'?: string;
    'aria-label'?: string;
  };
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: Status;
  presentation?: Presentation;
  fullHeight?: boolean;
};

export function Input({
  inputProps: {
    id,
    ref,
    placeholder,
    value,
    onBlur,
    onChange,
    onFocus,
    disabled,
    readOnly,
    'data-qa-id': dataQaId,
    'aria-label': ariaLabel,
  },
  prefix,
  suffix,
  status = 'neutral',
  presentation = 'default',
  fullHeight,
}: Props) {
  const inputId = useUniqueId({ prefix: 'input-group-', override: id });

  return (
    <div
      className={classNames(
        styles.container,
        {
          [styles.containerStatusNeutral]: status === 'neutral',
          [styles.containerStatusSuccess]: status === 'success',
          [styles.containerStatusError]: status === 'error',
        },
        {
          [styles.containerPresentationDefault]: presentation === 'default',
          [styles.containerPresentationContrast]: presentation === 'contrast',
          [styles.containerPresentationLightBorder]:
            presentation === 'light-border',
        },
        fullHeight ? styles.containerHeightFull : styles.containerHeightRegular
      )}
    >
      {prefix && (
        <label htmlFor={inputId} className={styles.affix}>
          {prefix}
        </label>
      )}

      <input
        id={inputId}
        className={styles.input}
        ref={ref}
        placeholder={placeholder}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly}
        data-qa-id={dataQaId}
        aria-label={ariaLabel}
      />

      {suffix && (
        <label htmlFor={inputId} className={styles.affix}>
          {suffix}
        </label>
      )}
    </div>
  );
}
